import { Component, type ErrorInfo, type ReactNode } from "react";
import * as Sentry from "@sentry/react";
import { AlertTriangle, RefreshCw } from "lucide-react";

import { ChunkReloadHint } from "@/components/ChunkReloadHint";
import { isChunkLoadError, requestChunkReload } from "@/lib/chunkReload";

// ---------------------------------------------------------------------------
// ErrorBoundary — Global Catch-Layer (Session 16b).
// ---------------------------------------------------------------------------
// Vorher: Render-Throw irgendwo im Baum → komplettes Blackscreen, keine
// Fehlermeldung. Daniel hat den Projekte-Blackscreen nach dem Persist-Rollout
// gemeldet — ohne Boundary war die Ursache unsichtbar.
// Jetzt: Fehler wird abgefangen, Stack an Sentry geschickt, der User sieht
// eine klare Meldung + einen „Cache leeren und neu laden"-Button.

interface Props {
  children: ReactNode;
  /** Optionaler Kontext-Name fuer Sentry (z.B. "ProjectsListPage"). */
  scope?: string;
}

interface State {
  error: Error | null;
}

export class ErrorBoundary extends Component<Props, State> {
  state: State = { error: null };

  static getDerivedStateFromError(error: Error): State {
    return { error };
  }

  componentDidCatch(error: Error, info: ErrorInfo): void {
    if (isChunkLoadError(error)) {
      // Stale-Deploy-Ladefehler, der die innere ChunkErrorBoundary umgangen
      // hat (z.B. lazy Dialog im TopNav ausserhalb des Outlets). Kein
      // Code-Bug → nicht an Sentry, stattdessen kontrollierter Reload.
      requestChunkReload();
      return;
    }
    Sentry.captureException(error, {
      tags: { scope: this.props.scope ?? "app" },
      extra: { componentStack: info.componentStack },
    });
  }

  private handleResetCache = (): void => {
    // Nuke den TanStack-Query-Persist-Cache + Last-Known-User.
    // Seite reloaden laesst AuthContext frisch starten.
    try {
      window.localStorage.removeItem("bergler.query-cache");
      window.localStorage.removeItem("bergler.last-known-user");
    } catch {
      // localStorage unerreichbar — Reload trotzdem versuchen.
    }
    window.location.reload();
  };

  render() {
    if (this.state.error) {
      if (isChunkLoadError(this.state.error)) {
        // Stale Deploy: kein Fehler-Look, sondern der Update-Hinweis.
        return <ChunkReloadHint fullscreen />;
      }
      return (
        <main className="flex min-h-screen items-center justify-center bg-background p-6">
          <div className="w-full max-w-lg rounded-md border border-destructive/40 bg-destructive/5 p-6">
            <div className="mb-4 flex items-start gap-3">
              <AlertTriangle className="mt-0.5 size-5 shrink-0 text-destructive" />
              <div>
                <h1 className="text-base font-semibold text-foreground">
                  Seite kann nicht angezeigt werden
                </h1>
                <p className="mt-1 text-xs text-muted-foreground">
                  Ein Render-Fehler hat die Seite gestoppt. Der Fehler wurde an
                  Sentry gemeldet. Oft löst ein Cache-Reset + Reload das
                  Problem (alter persistierter Query-Cache nach Schema-Update).
                </p>
              </div>
            </div>
            <pre className="mb-4 overflow-x-auto rounded-sm border border-border bg-muted/40 px-3 py-2 font-mono text-2xs leading-relaxed">
              {this.state.error.name}: {this.state.error.message}
            </pre>
            <button
              type="button"
              onClick={this.handleResetCache}
              className="inline-flex items-center gap-2 rounded-md bg-primary px-4 py-2 text-sm font-medium text-primary-foreground shadow hover:bg-primary/90"
            >
              <RefreshCw className="size-3.5" />
              Cache leeren und neu laden
            </button>
          </div>
        </main>
      );
    }
    return this.props.children;
  }
}
